import { useState, useCallback, useEffect } from 'react';
import { ValueOrFunction } from '../types/types';

// similar to useLocalStorage except that
// the storage is also keyed by the username.
export default function useUserLocalStorage<T>(
  username: string | undefined,
  key: string,
  initialValue: T
): [T, ValueOrFunction<T>, boolean] {
  const prefixedKey = username ? `${username}::${key}` : key;
  const [storedValue, setStoredValue] = useState<T>(initialValue);
  const [isReady, setIsReady] = useState(false);

  const fetchFromStorage = useCallback(
    (): T => {
      if (!username) return initialValue;
      try {
        const item = window.localStorage.getItem(prefixedKey);
        setIsReady(true);
        return item ? JSON.parse(item) : initialValue;
      } catch (error) {
        return initialValue;
      }
    },
    [username, prefixedKey, initialValue, setIsReady]
  );

  // If the username (or, less likely, initialValue) changes
  // restore the stored value from storage - including on initial render
  // (falls back to initialValue if not found)
  useEffect(() => {
    setIsReady(false);
    const newValue = fetchFromStorage();
    setStoredValue(newValue);
  }, [fetchFromStorage, setStoredValue, setIsReady]);

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      setStoredValue(prevValue => {
        const valueToStore = value instanceof Function ? value(prevValue) : value;
        window.localStorage.setItem(prefixedKey, JSON.stringify(valueToStore));
        return valueToStore;
      });
    } catch (error) {
      console.error(error);
    }
  }, [prefixedKey]);

  return [storedValue, setValue, isReady];
}
